import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import {
  Button,
  Card,
  DateInput,
  Label,
  NumberInput,
  PlusIcon,
  Select,
  Tabs,
  TabsContent,
  Textarea,
  TextInput,
  TrashIcon,
} from '../../components/ui';
import api from '../../services/api';
import type { CrewFormState } from './formMappers';
import { formToDashboardPreview } from './formMappers';
import type { CrewDocumentRow, CrewLookups, LookupItem } from './types';
import CrewDashboard from './CrewDashboard';

const newKey = () => `row-${Date.now()}-${Math.random()}`;

const lookupOptions = (items: LookupItem[] | undefined) =>
  (items ?? []).map((i) => ({
    value: String(i.id),
    label: i.abrv ? `${i.name} (${i.abrv})` : i.name,
  }));

const countryOptions = (items: CrewLookups['countries'] | undefined) =>
  (items ?? []).map((c) => ({
    value: String(c.id),
    label: c.isoCode2 ? `${c.name} (${c.isoCode2})` : c.name,
  }));

const portOptions = (items: CrewLookups['ports'] | undefined) =>
  (items ?? []).map((p) => ({
    value: String(p.id),
    label: p.code ? `${p.name} (${p.code})` : p.name,
  }));

const vesselOptions = (items: CrewLookups['vessels'] | undefined) =>
  (items ?? []).map((v) => ({
    value: String(v.id),
    label: v.vesselName,
  }));

const APPRAISAL_TYPE_OPTIONS = [
  { value: 'APPRAISAL', label: 'Appraisal' },
  { value: 'DISCHARGE', label: 'Discharge' },
];

const CHOICE_OPTIONS = [
  { value: 'YES', label: 'Yes' },
  { value: 'NO', label: 'No' },
  { value: 'NA', label: 'N/A' },
];

const scaleSelectOptions = (min: number, max: number, step: number): SelectOption[] => {
  const options: SelectOption[] = [];
  const safeStep = step >= 1 ? step : 1;
  for (let value = min; value <= max; value += safeStep) {
    options.push({ value: String(value), label: String(value) });
    if (options.length > 200) break;
  }
  return options;
};

const TRANSACTION_STATUS_OPTIONS = [
  { value: 'ONB', label: 'On board' },
  { value: 'OFB', label: 'Off board' },
];

const DEFAULT_MEDICAL_KIND_OPTIONS = [
  { value: 'FITNESS', label: 'Fitness' },
  { value: 'MEDICAL_EXAM', label: 'Medical exam' },
  { value: 'DRUG_ALCOHOL', label: 'Drug & alcohol' },
  { value: 'OTHER', label: 'Other' },
];

const DEFAULT_MEDICAL_RESULT_OPTIONS = [
  { value: 'FIT', label: 'Fit' },
  { value: 'UNFIT', label: 'Unfit' },
  { value: 'TEMPORARY_UNFIT', label: 'Temporary unfit' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'OTHER', label: 'Other' },
];

const DEFAULT_ALLOTTEE_FREQUENCY_OPTIONS = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'ONCE', label: 'Once' },
];

type SelectOption = { value: string; label: string };

type ContractColumn = NonNullable<CrewLookups['contractColumns']>[number];

const enumOptions = (values: string[] | undefined, fallback: SelectOption[]): SelectOption[] => {
  if (!values?.length) return fallback;
  return values.map((v) => ({
    value: v,
    label: fallback.find((o) => o.value === v)?.label ?? v.replace(/_/g, ' '),
  }));
};

const currencyOptions = (items: CrewLookups['currencies'] | undefined): SelectOption[] =>
  (items ?? []).map((c) => ({
    value: c.code,
    label: c.symbol ? `${c.code} — ${c.name} (${c.symbol})` : `${c.code} — ${c.name}`,
  }));

const contractColumnOptions = (items: CrewLookups['contractColumns'] | undefined): SelectOption[] =>
  [...(items ?? [])]
    .filter((c) => c.isActive !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((c) => ({ value: String(c.id), label: c.name }));

const sortedContractColumns = (items: CrewLookups['contractColumns'] | undefined): ContractColumn[] =>
  [...(items ?? [])]
    .filter((c) => c.isActive !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder);

const computeTotalWage = (
  wageValues: Record<string, string> | undefined,
  columns: ContractColumn[],
): string => {
  let total = 0;
  for (const col of columns) {
    if (!col.includeInTotal) continue;
    const raw = Number(wageValues?.[String(col.id)] ?? '');
    if (!Number.isFinite(raw)) continue;
    if (col.columnType === 'DEDUCTION') total -= raw;
    else if (col.columnType === 'EARNING') total += raw;
  }
  return Number.isFinite(total) ? String(total) : '';
};

const formatFileSize = (bytes: number | null | undefined) => {
  if (bytes == null || !Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const optionLabel = (options: SelectOption[], value: string | undefined) => {
  const v = value?.trim() ?? '';
  if (!v) return '-';
  return options.find((o) => o.value === v)?.label ?? v;
};

const displayText = (value: string | null | undefined) => (value?.trim() ? value : '-');
const displayDate = (value: string | null | undefined) => (value?.trim() ? formatDateGlobal(value) : '-');
const displayBool = (value: boolean | undefined) => (value ? 'Yes' : 'No');

export interface CrewFileTabsProps {
  form: CrewFormState;
  setForm: React.Dispatch<React.SetStateAction<CrewFormState>>;
  lookups: CrewLookups | undefined;
  readOnly?: boolean;
  crewId?: number;
  documents?: CrewDocumentRow[];
}

const gridClass = 'grid md:grid-cols-2 lg:grid-cols-3 gap-4';

const CREW_NAV_GROUPS: {
  title: string;
  sections: {
    value: string;
    label: string;
    description?: string;
    countKey?: keyof CrewFormState;
  }[];
}[] = [
  {
    title: 'Overview',
    sections: [
      { value: 'dashboard', label: 'Dashboard', description: 'Summary and key crew metrics' },
    ],
  },
  {
    title: 'Profile',
    sections: [
      { value: 'personal', label: 'Personal', description: 'Identity, rank, and biographical details' },
      { value: 'contact', label: 'Contact', description: 'Phone, email, and addresses' },
      { value: 'availability', label: 'Availability', description: 'Employment status and manning' },
    ],
  },
  {
    title: 'Records',
    sections: [
      { value: 'certs', label: 'Certificates', countKey: 'certs', description: 'Licenses and certifications' },
      { value: 'allottees', label: 'Allottees', countKey: 'allottees', description: 'Salary allotments' },
      { value: 'nok', label: 'Next of kin', countKey: 'nextOfKin', description: 'Emergency contacts' },
      { value: 'appraisals', label: 'Appraisals', countKey: 'appraisals', description: 'Evaluations, appraisals, discharge and briefing forms' },
      { value: 'feedback', label: 'Feedback', countKey: 'feedback', description: 'Feedback records' },
      { value: 'prevService', label: 'Previous service', countKey: 'prevServices', description: 'Prior vessel employment' },
      { value: 'transactions', label: 'Transactions', countKey: 'transactions', description: 'Sign on / sign off history' },
      { value: 'medicals', label: 'Medical', countKey: 'medicals', description: 'Fitness and medical exams' },
      { value: 'documents', label: 'Documents', description: 'Uploaded crew files' },
    ],
  },
];

const ALL_CREW_SECTIONS = CREW_NAV_GROUPS.flatMap((group) => group.sections);

type SectionAccent = {
  active: string;
  badge: string;
  groupLabel: string;
};

const SECTION_ACCENTS: Record<string, SectionAccent> = {
  dashboard: {
    active: 'border-l-cyan-500 bg-cyan-50 text-cyan-900 shadow-sm dark:border-l-cyan-400 dark:bg-cyan-950/50 dark:text-cyan-100',
    badge: 'bg-cyan-200 text-cyan-900 dark:bg-cyan-800 dark:text-cyan-100',
    groupLabel: 'text-cyan-600 dark:text-cyan-400',
  },
  personal: {
    active: 'border-l-indigo-500 bg-indigo-50 text-indigo-900 shadow-sm dark:border-l-indigo-400 dark:bg-indigo-950/50 dark:text-indigo-100',
    badge: 'bg-indigo-200 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-100',
    groupLabel: 'text-indigo-600 dark:text-indigo-400',
  },
  contact: {
    active: 'border-l-sky-500 bg-sky-50 text-sky-900 shadow-sm dark:border-l-sky-400 dark:bg-sky-950/50 dark:text-sky-100',
    badge: 'bg-sky-200 text-sky-800 dark:bg-sky-800 dark:text-sky-100',
    groupLabel: 'text-sky-600 dark:text-sky-400',
  },
  availability: {
    active: 'border-l-emerald-500 bg-emerald-50 text-emerald-900 shadow-sm dark:border-l-emerald-400 dark:bg-emerald-950/50 dark:text-emerald-100',
    badge: 'bg-emerald-200 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-100',
    groupLabel: 'text-emerald-600 dark:text-emerald-400',
  },
  certs: {
    active: 'border-l-amber-500 bg-amber-50 text-amber-900 shadow-sm dark:border-l-amber-400 dark:bg-amber-950/50 dark:text-amber-100',
    badge: 'bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100',
    groupLabel: 'text-amber-600 dark:text-amber-400',
  },
  allottees: {
    active: 'border-l-violet-500 bg-violet-50 text-violet-900 shadow-sm dark:border-l-violet-400 dark:bg-violet-950/50 dark:text-violet-100',
    badge: 'bg-violet-200 text-violet-800 dark:bg-violet-800 dark:text-violet-100',
    groupLabel: 'text-violet-600 dark:text-violet-400',
  },
  nok: {
    active: 'border-l-rose-500 bg-rose-50 text-rose-900 shadow-sm dark:border-l-rose-400 dark:bg-rose-950/50 dark:text-rose-100',
    badge: 'bg-rose-200 text-rose-800 dark:bg-rose-800 dark:text-rose-100',
    groupLabel: 'text-rose-600 dark:text-rose-400',
  },
  appraisals: {
    active: 'border-l-teal-500 bg-teal-50 text-teal-900 shadow-sm dark:border-l-teal-400 dark:bg-teal-950/50 dark:text-teal-100',
    badge: 'bg-teal-200 text-teal-800 dark:bg-teal-800 dark:text-teal-100',
    groupLabel: 'text-teal-600 dark:text-teal-400',
  },
  feedback: {
    active: 'border-l-orange-500 bg-orange-50 text-orange-900 shadow-sm dark:border-l-orange-400 dark:bg-orange-950/50 dark:text-orange-100',
    badge: 'bg-orange-200 text-orange-900 dark:bg-orange-800 dark:text-orange-100',
    groupLabel: 'text-orange-600 dark:text-orange-400',
  },
  prevService: {
    active: 'border-l-cyan-500 bg-cyan-50 text-cyan-900 shadow-sm dark:border-l-cyan-400 dark:bg-cyan-950/50 dark:text-cyan-100',
    badge: 'bg-cyan-200 text-cyan-900 dark:bg-cyan-800 dark:text-cyan-100',
    groupLabel: 'text-cyan-600 dark:text-cyan-400',
  },
  transactions: {
    active: 'border-l-blue-500 bg-blue-50 text-blue-900 shadow-sm dark:border-l-blue-400 dark:bg-blue-950/50 dark:text-blue-100',
    badge: 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-100',
    groupLabel: 'text-blue-600 dark:text-blue-400',
  },
  medicals: {
    active: 'border-l-lime-500 bg-lime-50 text-lime-900 shadow-sm dark:border-l-lime-400 dark:bg-lime-950/50 dark:text-lime-100',
    badge: 'bg-lime-200 text-lime-900 dark:bg-lime-800 dark:text-lime-100',
    groupLabel: 'text-lime-600 dark:text-lime-400',
  },
  documents: {
    active: 'border-l-slate-500 bg-slate-50 text-slate-900 shadow-sm dark:border-l-slate-400 dark:bg-slate-950/50 dark:text-slate-100',
    badge: 'bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100',
    groupLabel: 'text-slate-600 dark:text-slate-400',
  },
};

const FIELD_GROUP_ACCENTS: Record<string, { bar: string; header: string; title: string }> = {
  'Identity & reference': {
    bar: 'border-l-indigo-500',
    header: 'bg-gradient-to-r from-indigo-50 to-violet-50 dark:from-indigo-950/40 dark:to-violet-950/30',
    title: 'text-indigo-900 dark:text-indigo-100',
  },
  'Rank & nationality': {
    bar: 'border-l-sky-500',
    header: 'bg-gradient-to-r from-sky-50 to-cyan-50 dark:from-sky-950/40 dark:to-cyan-950/30',
    title: 'text-sky-900 dark:text-sky-100',
  },
  'Birth & family': {
    bar: 'border-l-rose-500',
    header: 'bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-950/40 dark:to-pink-950/30',
    title: 'text-rose-900 dark:text-rose-100',
  },
  'Physical attributes': {
    bar: 'border-l-amber-500',
    header: 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/40 dark:to-orange-950/30',
    title: 'text-amber-900 dark:text-amber-100',
  },
  'Phone & messaging': {
    bar: 'border-l-teal-500',
    header: 'bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-950/40 dark:to-emerald-950/30',
    title: 'text-teal-900 dark:text-teal-100',
  },
  Email: {
    bar: 'border-l-violet-500',
    header: 'bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-950/40 dark:to-purple-950/30',
    title: 'text-violet-900 dark:text-violet-100',
  },
  Addresses: {
    bar: 'border-l-cyan-500',
    header: 'bg-gradient-to-r from-cyan-50 to-sky-50 dark:from-cyan-950/40 dark:to-sky-950/30',
    title: 'text-cyan-900 dark:text-cyan-100',
  },
  'Location & banking': {
    bar: 'border-l-blue-500',
    header: 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/30',
    title: 'text-blue-900 dark:text-blue-100',
  },
  'Availability status': {
    bar: 'border-l-emerald-500',
    header: 'bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30',
    title: 'text-emerald-900 dark:text-emerald-100',
  },
  'Activity dates': {
    bar: 'border-l-orange-500',
    header: 'bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/30',
    title: 'text-orange-900 dark:text-orange-100',
  },
  'Notes & duties': {
    bar: 'border-l-fuchsia-500',
    header: 'bg-gradient-to-r from-fuchsia-50 to-pink-50 dark:from-fuchsia-950/40 dark:to-pink-950/30',
    title: 'text-fuchsia-900 dark:text-fuchsia-100',
  },
};

const DEFAULT_FIELD_GROUP_ACCENT = {
  bar: 'border-l-slate-400',
  header: 'bg-slate-50/80 dark:bg-slate-800/40',
  title: '',
};

const sectionCount = (form: CrewFormState, countKey?: keyof CrewFormState): number | null => {
  if (!countKey) return null;
  const value = form[countKey];
  return Array.isArray(value) ? value.length : null;
};

type CertFormRow = CrewFormState['certs'][number];
type AllotteeFormRow = CrewFormState['allottees'][number];
type NokFormRow = CrewFormState['nextOfKin'][number];
type AppraisalFormRow = CrewFormState['appraisals'][number];
type FeedbackFormRow = CrewFormState['feedback'][number];
type PrevServiceFormRow = CrewFormState['prevServices'][number];
type TransactionFormRow = CrewFormState['transactions'][number];
type MedicalFormRow = CrewFormState['medicals'][number];

const CrewFileTabs: React.FC<CrewFileTabsProps> = ({
  form,
  setForm,
  lookups,
  readOnly = false,
  crewId = 0,
  documents: documentsProp = [],
}) => {
  const { colors } = useTheme();
  const [activeSection, setActiveSection] = React.useState('dashboard');
  const [documents, setDocuments] = React.useState<CrewDocumentRow[]>(documentsProp);
  const [docUploading, setDocUploading] = React.useState(false);
  const [docError, setDocError] = React.useState('');
  const [wageImportErrors, setWageImportErrors] = React.useState<Record<string, string>>({});
  const [wageImportingKey, setWageImportingKey] = React.useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const activeMeta = ALL_CREW_SECTIONS.find((section) => section.value === activeSection);
  const dashboardPreview = React.useMemo(
    () => formToDashboardPreview(form, lookups, crewId),
    [form, lookups, crewId],
  );

  React.useEffect(() => {
    setDocuments(documentsProp);
  }, [documentsProp]);

  const rankOpts = React.useMemo(() => lookupOptions(lookups?.ranks), [lookups?.ranks]);
  const nationalityOpts = React.useMemo(() => lookupOptions(lookups?.nationalities), [lookups?.nationalities]);
  const sexTypeOpts = React.useMemo(() => lookupOptions(lookups?.sexTypes), [lookups?.sexTypes]);
  const maritalStatusOpts = React.useMemo(() => lookupOptions(lookups?.maritalStatuses), [lookups?.maritalStatuses]);
  const religionOpts = React.useMemo(() => lookupOptions(lookups?.religions), [lookups?.religions]);
  const relationOpts = React.useMemo(() => lookupOptions(lookups?.relations), [lookups?.relations]);
  const manningAgentOpts = React.useMemo(() => lookupOptions(lookups?.manningAgents), [lookups?.manningAgents]);
  const availabilityStatusOpts = React.useMemo(
    () => lookupOptions(lookups?.availabilityStatuses),
    [lookups?.availabilityStatuses],
  );
  const certCatalogOpts = React.useMemo(() => lookupOptions(lookups?.certCatalogs), [lookups?.certCatalogs]);
  const employmentStatusOpts = React.useMemo(
    () => lookupOptions(lookups?.employmentStatuses),
    [lookups?.employmentStatuses],
  );
  const vesselTypeOpts = React.useMemo(() => lookupOptions(lookups?.vesselTypes), [lookups?.vesselTypes]);
  const engineTypeOpts = React.useMemo(() => lookupOptions(lookups?.engineTypes), [lookups?.engineTypes]);
  const signOffReasonOpts = React.useMemo(() => lookupOptions(lookups?.signOffReasons), [lookups?.signOffReasons]);
  const questionnaireKindOpts = React.useMemo(
    () => lookupOptions(lookups?.questionnaireKinds),
    [lookups?.questionnaireKinds],
  );
  const countryOpts = React.useMemo(() => countryOptions(lookups?.countries), [lookups?.countries]);
  const portOpts = React.useMemo(() => portOptions(lookups?.ports), [lookups?.ports]);
  const vesselOpts = React.useMemo(() => vesselOptions(lookups?.vessels), [lookups?.vessels]);
  const currencyOpts = React.useMemo(() => currencyOptions(lookups?.currencies), [lookups?.currencies]);
  const contractColumns = React.useMemo(() => sortedContractColumns(lookups?.contractColumns), [lookups?.contractColumns]);
  const contractColumnOpts = React.useMemo(
    () => contractColumnOptions(lookups?.contractColumns),
    [lookups?.contractColumns],
  );
  const medicalKindOpts = React.useMemo(
    () => enumOptions(lookups?.medicalKinds, DEFAULT_MEDICAL_KIND_OPTIONS),
    [lookups?.medicalKinds],
  );
  const medicalResultOpts = React.useMemo(
    () => enumOptions(lookups?.medicalResults, DEFAULT_MEDICAL_RESULT_OPTIONS),
    [lookups?.medicalResults],
  );
  const allotteeFrequencyOpts = React.useMemo(
    () => enumOptions(lookups?.allotteeFrequencies, DEFAULT_ALLOTTEE_FREQUENCY_OPTIONS),
    [lookups?.allotteeFrequencies],
  );

  const readOnlyTextClass = cn('text-sm block min-h-[1.25rem]', colors.text);

  const applyForm = (updater: (state: CrewFormState) => CrewFormState | Record<string, unknown>) => {
    setForm((state) => updater(state) as CrewFormState);
  };

  const renderTextField = (
    label: string,
    id: string,
    value: string,
    onChange: (value: string) => void,
    options?: { required?: boolean; placeholder?: string; multiline?: boolean; span?: boolean },
  ) => (
    <div className={cn('min-w-0', options?.span && 'md:col-span-2 lg:col-span-3')}>
      <Label htmlFor={readOnly ? undefined : id} required={options?.required}>
        {label}
      </Label>
      {readOnly ? (
        <span className={readOnlyTextClass}>{displayText(value)}</span>
      ) : options?.multiline ? (
        <Textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={options?.placeholder}
          rows={3}
        />
      ) : (
        <TextInput
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={options?.placeholder}
        />
      )}
    </div>
  );

  const renderSelectField = (
    label: string,
    id: string,
    value: string,
    options: SelectOption[],
    onChange: (value: string) => void,
    fieldOptions?: { required?: boolean; placeholder?: string; span?: boolean },
  ) => (
    <div className={cn('min-w-0', fieldOptions?.span && 'md:col-span-2 lg:col-span-3')}>
      <Label htmlFor={readOnly ? undefined : id} required={fieldOptions?.required}>
        {label}
      </Label>
      {readOnly ? (
        <span className={readOnlyTextClass}>{optionLabel(options, value)}</span>
      ) : (
        <Select
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          options={[{ value: '', label: fieldOptions?.placeholder ?? 'Select…' }, ...options]}
          placeholder={fieldOptions?.placeholder ?? 'Select…'}
        />
      )}
    </div>
  );

  const renderDateField = (
    label: string,
    id: string,
    value: string,
    onChange: (value: string) => void,
  ) => (
    <div className="min-w-0">
      <Label htmlFor={readOnly ? undefined : id}>{label}</Label>
      {readOnly ? (
        <span className={readOnlyTextClass}>{displayDate(value)}</span>
      ) : (
        <DateInput id={id} value={value} onChange={(e) => onChange(e.target.value)} showFormatHint={false} />
      )}
    </div>
  );

  const renderNumberField = (
    label: string,
    id: string,
    value: string,
    onChange: (value: string) => void,
  ) => (
    <div className="min-w-0">
      <Label htmlFor={readOnly ? undefined : id}>{label}</Label>
      {readOnly ? (
        <span className={readOnlyTextClass}>{displayText(value)}</span>
      ) : (
        <NumberInput
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value != null ? String(e.target.value) : '')}
        />
      )}
    </div>
  );

  const renderCheckboxField = (
    label: string,
    id: string,
    checked: boolean,
    onChange: (checked: boolean) => void,
  ) => (
    <div className="min-w-0 flex items-end pb-1">
      {readOnly ? (
        <div>
          <Label>{label}</Label>
          <span className={readOnlyTextClass}>{displayBool(checked)}</span>
        </div>
      ) : (
        <label htmlFor={id} className={cn('flex items-center gap-2 text-sm cursor-pointer', colors.text)}>
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            className="rounded"
          />
          {label}
        </label>
      )}
    </div>
  );

  const renderFieldGroup = (
    title: string,
    description: string | undefined,
    children: React.ReactNode,
  ) => {
    const accent = FIELD_GROUP_ACCENTS[title] ?? DEFAULT_FIELD_GROUP_ACCENT;
    return (
      <section className={cn('rounded-lg border shadow-sm overflow-hidden border-l-4', colors.surface, colors.border, accent.bar)}>
        <div className={cn('border-b px-5 py-3', colors.border, accent.header)}>
          <h3 className={cn('text-sm font-semibold', accent.title || colors.text)}>{title}</h3>
          {description && (
            <p className={cn('text-xs mt-0.5 opacity-80', accent.title || colors.textMuted)}>{description}</p>
          )}
        </div>
        <div className={cn('p-5', gridClass)}>{children}</div>
      </section>
    );
  };

  const renderInlineFieldGroup = (title: string, children: React.ReactNode) => {
    const accent = FIELD_GROUP_ACCENTS[title];
    return (
      <div className="space-y-3">
        <p
          className={cn(
            'text-xs font-semibold uppercase tracking-wide border-b pb-2 border-l-4 pl-2',
            accent?.title ?? colors.textMuted,
            accent?.bar ?? 'border-l-slate-300',
          )}
        >
          {title}
        </p>
        <div className={gridClass}>{children}</div>
      </div>
    );
  };

  const removeRow = (
    key: 'certs' | 'allottees' | 'nextOfKin' | 'appraisals' | 'feedback' | 'prevServices' | 'transactions' | 'medicals',
    rowKey: string,
  ) => {
    applyForm((s) => {
      switch (key) {
        case 'certs':
          return { ...s, certs: (s.certs as CertFormRow[]).filter((row) => row.key !== rowKey) };
        case 'allottees':
          return { ...s, allottees: (s.allottees as AllotteeFormRow[]).filter((row) => row.key !== rowKey) };
        case 'nextOfKin':
          return { ...s, nextOfKin: (s.nextOfKin as NokFormRow[]).filter((row) => row.key !== rowKey) };
        case 'appraisals':
          return { ...s, appraisals: (s.appraisals as AppraisalFormRow[]).filter((row) => row.key !== rowKey) };
        case 'feedback':
          return { ...s, feedback: (s.feedback as FeedbackFormRow[]).filter((row) => row.key !== rowKey) };
        case 'prevServices':
          return { ...s, prevServices: (s.prevServices as PrevServiceFormRow[]).filter((row) => row.key !== rowKey) };
        case 'transactions':
          return { ...s, transactions: (s.transactions as TransactionFormRow[]).filter((row) => row.key !== rowKey) };
        case 'medicals':
          return { ...s, medicals: (s.medicals as MedicalFormRow[]).filter((row) => row.key !== rowKey) };
        default:
          return s;
      }
    });
  };

  const updateTransaction = (rowKey: string, patch: Partial<TransactionFormRow>) => {
    applyForm((s) => ({
      ...s,
      transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === rowKey ? { ...t, ...patch } : t)),
    }));
  };

  const setTransactionWageValue = (rowKey: string, columnId: string, amount: string) => {
    applyForm((s) => ({
      ...s,
      transactions: (s.transactions as TransactionFormRow[]).map((t) => {
        if (t.key !== rowKey) return t;
        const wageValues = { ...(t.wageValues ?? {}), [columnId]: amount };
        return { ...t, wageValues, totalWage: computeTotalWage(wageValues, contractColumns) };
      }),
    }));
  };

  const importWageScale = async (row: TransactionFormRow) => {
    if (!row.vesselId || !row.rankId || !form.nationalityId) {
      setWageImportErrors((prev) => ({
        ...prev,
        [row.key]: 'Select vessel and rank on this transaction, and nationality on the Personal tab.',
      }));
      return;
    }
    setWageImportingKey(row.key);
    setWageImportErrors((prev) => {
      const next = { ...prev };
      delete next[row.key];
      return next;
    });
    try {
      const asOf = row.signOnDate || new Date().toISOString().slice(0, 10);
      const { data: scale } = await api.get('/crew-wage/scales/resolve', {
        params: {
          vesselId: row.vesselId,
          rankId: row.rankId,
          nationalityId: form.nationalityId,
          asOf,
        },
        skipGlobalErrorToast: true,
      });
      const wageValues: Record<string, string> = {};
      for (const wv of scale.values ?? []) {
        wageValues[String(wv.columnId)] = wv.amount != null ? String(wv.amount) : '';
      }
      updateTransaction(row.key, {
        wageValues,
        wageCurrency: scale.currency ?? '',
        wageScaleId: scale.id != null ? String(scale.id) : '',
        totalWage: computeTotalWage(wageValues, contractColumns),
      });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { message?: string | string[] } } })?.response?.status;
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      const message =
        status === 404
          ? 'No wage scale found for this vessel, rank and nationality.'
          : Array.isArray(msg)
            ? msg.join(' ')
            : typeof msg === 'string'
              ? msg
              : 'Failed to import wage scale.';
      setWageImportErrors((prev) => ({ ...prev, [row.key]: message }));
    } finally {
      setWageImportingKey(null);
    }
  };

  const uploadDocuments = async (files: FileList | null) => {
    if (!crewId || !files?.length) return;
    setDocUploading(true);
    setDocError('');
    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => formData.append('files', file));
      const { data } = await api.post(`/crew/${crewId}/files`, formData);
      const created = Array.isArray(data) ? data : data ? [data] : [];
      setDocuments((prev) => [...prev, ...created]);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setDocError(Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : 'Upload failed.');
    } finally {
      setDocUploading(false);
    }
  };

  const deleteDocument = async (fileId: number) => {
    if (!crewId) return;
    setDocError('');
    try {
      await api.delete(`/crew/${crewId}/files/${fileId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== fileId));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      setDocError(Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : 'Delete failed.');
    }
  };

  const downloadDocument = async (doc: CrewDocumentRow) => {
    if (!crewId) return;
    try {
      const response = await api.get(`/crew/${crewId}/files/${doc.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.originalFilename || `crew-file-${doc.id}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setDocError('Download failed.');
    }
  };

  return (
    <Tabs
      value={activeSection}
      onValueChange={setActiveSection}
      className="flex flex-col lg:flex-row flex-1 min-h-0 overflow-hidden"
    >
      <nav
        className={cn(
          'flex lg:flex-col lg:w-60 shrink-0 gap-1 p-2 overflow-x-auto lg:overflow-y-auto dashboard-scrollbar',
          'border-b lg:border-b-0 lg:border-r',
          colors.border,
          'bg-gradient-to-b from-slate-50 via-indigo-50/30 to-violet-50/40 dark:from-slate-900 dark:via-indigo-950/20 dark:to-violet-950/20',
        )}
        aria-label="Crew file sections"
      >
        {CREW_NAV_GROUPS.map((group, groupIndex) => {
          const groupAccent = SECTION_ACCENTS[group.sections[0]?.value ?? '']?.groupLabel ?? colors.textMuted;
          return (
          <React.Fragment key={group.title}>
            {groupIndex > 0 && (
              <div className={cn('hidden lg:block my-1.5 border-t', colors.border)} aria-hidden />
            )}
            <p
              className={cn(
                'hidden lg:block px-2 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider',
                groupAccent,
              )}
            >
              {group.title}
            </p>
            {group.sections.map((section) => {
              const isActive = activeSection === section.value;
              const count = sectionCount(form, section.countKey);
              const accent = SECTION_ACCENTS[section.value];
              return (
                <button
                  key={section.value}
                  type="button"
                  onClick={() => setActiveSection(section.value)}
                  className={cn(
                    'flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-all shrink-0 lg:shrink lg:w-full',
                    'border-l-[3px] text-left',
                    isActive && accent
                      ? accent.active
                      : cn('border-l-transparent', colors.textSecondary, 'hover:bg-white/90 dark:hover:bg-slate-800/70'),
                  )}
                >
                  <span className="truncate">{section.label}</span>
                  {count != null && count > 0 && (
                    <span
                      className={cn(
                        'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                        isActive && accent ? accent.badge : 'bg-slate-200/90 text-slate-700 dark:bg-slate-600 dark:text-slate-100',
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </React.Fragment>
        );})}
      </nav>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {activeSection !== 'dashboard' && (
        <div
          className={cn(
            'flex-shrink-0 px-5 py-4 border-b bg-gradient-to-r from-white to-indigo-50/50 dark:from-slate-900 dark:to-indigo-950/30',
            colors.border,
          )}
        >
          <h2 className={cn('text-lg font-semibold', SECTION_ACCENTS[activeSection]?.groupLabel ?? colors.text)}>
            {activeMeta?.label ?? 'Section'}
          </h2>
          {activeMeta?.description && (
            <p className={cn('text-sm mt-0.5', colors.textMuted)}>{activeMeta.description}</p>
          )}
        </div>
        )}

        <div className="flex-1 overflow-y-auto dashboard-scrollbar min-h-0 p-5">
      <TabsContent value="dashboard" className="mt-0">
        <CrewDashboard item={dashboardPreview} preview={!readOnly} />
      </TabsContent>

      <TabsContent value="personal" className="mt-0">
        <div className="space-y-5">
          {renderFieldGroup(
            'Identity & reference',
            'Legal name and internal crew reference',
            <>
              {renderTextField('Last name', 'crew-lastName', form.lastName, (v) => applyForm((s) => ({ ...s, lastName: v })), {
                required: true,
              })}
              {renderTextField('Middle name', 'crew-middleName', form.middleName ?? '', (v) =>
                applyForm((s) => ({ ...s, middleName: v })),
              )}
              {renderTextField('First name', 'crew-firstName', form.firstName, (v) => applyForm((s) => ({ ...s, firstName: v })), {
                required: true,
              })}
              {renderTextField('Crew ref', 'crew-crewRef', form.crewRef ?? '', (v) => applyForm((s) => ({ ...s, crewRef: v })))}
              {renderSelectField(
                'Sex',
                'crew-sexTypeId',
                form.sexTypeId,
                sexTypeOpts,
                (v) => applyForm((s) => ({ ...s, sexTypeId: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Rank & nationality',
            'Position, nationality, and manning agent',
            <>
              {renderSelectField('Rank', 'crew-rankId', form.rankId, rankOpts, (v) => applyForm((s) => ({ ...s, rankId: v })))}
              {renderSelectField(
                'Nationality',
                'crew-nationalityId',
                form.nationalityId,
                nationalityOpts,
                (v) => applyForm((s) => ({ ...s, nationalityId: v })),
              )}
              {renderSelectField(
                'Manning agent',
                'crew-manningAgentId',
                form.manningAgentId,
                manningAgentOpts,
                (v) => applyForm((s) => ({ ...s, manningAgentId: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Birth & family',
            'Birth details, parents, and marital information',
            <>
              {renderDateField('Birth date', 'crew-birthDate', form.birthDate, (v) => applyForm((s) => ({ ...s, birthDate: v })))}
              {renderTextField('Birth place', 'crew-birthPlace', form.birthPlace ?? '', (v) =>
                applyForm((s) => ({ ...s, birthPlace: v })),
              )}
              {renderTextField('Father name', 'crew-fatherName', form.fatherName ?? '', (v) =>
                applyForm((s) => ({ ...s, fatherName: v })),
              )}
              {renderTextField('Mother name', 'crew-motherName', form.motherName ?? '', (v) =>
                applyForm((s) => ({ ...s, motherName: v })),
              )}
              {renderSelectField(
                'Marital status',
                'crew-maritalStatusId',
                form.maritalStatusId,
                maritalStatusOpts,
                (v) => applyForm((s) => ({ ...s, maritalStatusId: v })),
              )}
              {renderSelectField(
                'Religion',
                'crew-religionId',
                form.religionId,
                religionOpts,
                (v) => applyForm((s) => ({ ...s, religionId: v })),
              )}
              {renderTextField('Wife name', 'crew-wifeName', form.wifeName ?? '', (v) => applyForm((s) => ({ ...s, wifeName: v })))}
              {renderNumberField('No. of children', 'crew-noOfChildren', String(form.noOfChildren ?? ''), (v) =>
                applyForm((s) => ({ ...s, noOfChildren: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Physical attributes',
            'Appearance and uniform sizes',
            <>
              {renderTextField('Eye color', 'crew-eyeColor', form.eyeColor ?? '', (v) => applyForm((s) => ({ ...s, eyeColor: v })))}
              {renderTextField('Hair color', 'crew-hairColor', form.hairColor ?? '', (v) =>
                applyForm((s) => ({ ...s, hairColor: v })),
              )}
              {renderTextField('Height', 'crew-crewHeight', form.crewHeight ?? '', (v) =>
                applyForm((s) => ({ ...s, crewHeight: v })),
              )}
              {renderTextField('Weight', 'crew-crewWeight', form.crewWeight ?? '', (v) =>
                applyForm((s) => ({ ...s, crewWeight: v })),
              )}
              {renderTextField('Shoes size', 'crew-shoesSize', form.shoesSize ?? '', (v) =>
                applyForm((s) => ({ ...s, shoesSize: v })),
              )}
              {renderTextField('Working clothes size', 'crew-workingClothesSize', form.workingClothesSize ?? '', (v) =>
                applyForm((s) => ({ ...s, workingClothesSize: v })),
              )}
            </>,
          )}
        </div>
      </TabsContent>

      <TabsContent value="contact" className="mt-0">
        <div className="space-y-5">
          {renderFieldGroup(
            'Phone & messaging',
            'Telephone, mobile, fax, and Skype',
            <>
              {renderTextField('Telephone 1', 'crew-telephone1', form.telephone1 ?? '', (v) =>
                applyForm((s) => ({ ...s, telephone1: v })),
              )}
              {renderTextField('Telephone 2', 'crew-telephone2', form.telephone2 ?? '', (v) =>
                applyForm((s) => ({ ...s, telephone2: v })),
              )}
              {renderTextField('Fax', 'crew-fax1', form.fax1 ?? '', (v) => applyForm((s) => ({ ...s, fax1: v })))}
              {renderTextField('Mobile 1', 'crew-mobile1', form.mobile1 ?? '', (v) => applyForm((s) => ({ ...s, mobile1: v })))}
              {renderTextField('Mobile 2', 'crew-mobile2', form.mobile2 ?? '', (v) => applyForm((s) => ({ ...s, mobile2: v })))}
              {renderTextField('Skype', 'crew-skypeAccount', form.skypeAccount ?? '', (v) =>
                applyForm((s) => ({ ...s, skypeAccount: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Email',
            'Primary and secondary email addresses',
            <>
              {renderTextField('Email 1', 'crew-email1', form.email1 ?? '', (v) => applyForm((s) => ({ ...s, email1: v })))}
              {renderTextField('Email 2', 'crew-email2', form.email2 ?? '', (v) => applyForm((s) => ({ ...s, email2: v })))}
            </>,
          )}
          {renderFieldGroup(
            'Addresses',
            'Home, temporary, and country of residence',
            <>
              {renderTextField('Home address', 'crew-homeAddress', form.homeAddress ?? '', (v) =>
                applyForm((s) => ({ ...s, homeAddress: v })),
                { multiline: true, span: true },
              )}
              {renderTextField('Temporary address', 'crew-tempAddress', form.tempAddress ?? '', (v) =>
                applyForm((s) => ({ ...s, tempAddress: v })),
                { multiline: true, span: true },
              )}
              {renderSelectField(
                'Country',
                'crew-countryId',
                form.countryId,
                countryOpts,
                (v) => applyForm((s) => ({ ...s, countryId: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Location & banking',
            'Nearest travel points and bank account',
            <>
              {renderSelectField(
                'Nearest port',
                'crew-nearestPortId',
                form.nearestPortId,
                portOpts,
                (v) => applyForm((s) => ({ ...s, nearestPortId: v })),
              )}
              {renderTextField('Nearest airport', 'crew-nearestAirport', form.nearestAirport ?? '', (v) =>
                applyForm((s) => ({ ...s, nearestAirport: v })),
              )}
              {renderTextField('Bank account', 'crew-bankAccount', form.bankAccount ?? '', (v) =>
                applyForm((s) => ({ ...s, bankAccount: v })),
              )}
            </>,
          )}
        </div>
      </TabsContent>

      <TabsContent value="availability" className="mt-0">
        <div className="space-y-5">
          {renderFieldGroup(
            'Availability status',
            'Current employment availability window',
            <>
              {renderSelectField(
                'Availability status',
                'crew-availabilityStatusId',
                form.availabilityStatusId,
                availabilityStatusOpts,
                (v) => applyForm((s) => ({ ...s, availabilityStatusId: v })),
              )}
              {renderDateField('Available from', 'crew-availableFrom', form.availableFrom, (v) =>
                applyForm((s) => ({ ...s, availableFrom: v })),
              )}
              {renderDateField('Available to', 'crew-availableTo', form.availableTo, (v) =>
                applyForm((s) => ({ ...s, availableTo: v })),
              )}
              {renderCheckboxField('Not recommended', 'crew-notRecommended', form.notRecommended ?? false, (v) =>
                applyForm((s) => ({ ...s, notRecommended: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Activity dates',
            'Last application and contact dates',
            <>
              {renderDateField('Last application date', 'crew-lastApplicationDate', form.lastApplicationDate, (v) =>
                applyForm((s) => ({ ...s, lastApplicationDate: v })),
              )}
              {renderDateField('Last contacted date', 'crew-lastContactedDate', form.lastContactedDate, (v) =>
                applyForm((s) => ({ ...s, lastContactedDate: v })),
              )}
            </>,
          )}
          {renderFieldGroup(
            'Notes & duties',
            'Duties and free-text notes about availability',
            <>
              {renderTextField('Duties', 'crew-duties', form.duties ?? '', (v) => applyForm((s) => ({ ...s, duties: v })), {
                span: true,
              })}
              {renderTextField(
                'Availability notes',
                'crew-availabilityNotes',
                form.availabilityNotes ?? '',
                (v) => applyForm((s) => ({ ...s, availabilityNotes: v })),
                { multiline: true, span: true },
              )}
              {renderTextField('Crew notes', 'crew-crewNotes', form.crewNotes ?? '', (v) =>
                applyForm((s) => ({ ...s, crewNotes: v })),
                { multiline: true, span: true },
              )}
            </>,
          )}
        </div>
      </TabsContent>

      <TabsContent value="certs" className="mt-0">
        {!readOnly && (
          <div className="mb-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() =>
                applyForm((s) => ({
                  ...s,
                  certs: [
                    ...s.certs,
                    {
                      key: newKey(),
                      certId: '',
                      issueCountryId: '',
                      certNumber: '',
                      issuedDate: '',
                      expiredDate: '',
                      onVessel: false,
                      certFileName: '',
                      notes: '',
                    },
                  ],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add certificate
            </Button>
          </div>
        )}
        <div className="space-y-4">
          {form.certs.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No certificates recorded.</p>
          ) : (
            form.certs.map((row: CertFormRow) => (
              <Card key={row.key} className="p-4">
                {!readOnly && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Remove certificate"
                      aria-label="Remove certificate"
                      className={cn(colors.textSecondary, 'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25')}
                      onClick={() => removeRow('certs', row.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <div className="space-y-5">
                  {renderInlineFieldGroup(
                    'Certificate details',
                    <>
                      {renderSelectField(
                        'Certificate',
                        `cert-certId-${row.key}`,
                        row.certId,
                        certCatalogOpts,
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            certs: (s.certs as CertFormRow[]).map((c) => (c.key === row.key ? { ...c, certId: v } : c)),
                          })),
                        { required: true },
                      )}
                      {renderSelectField(
                        'Issue country',
                        `cert-issueCountryId-${row.key}`,
                        row.issueCountryId,
                        countryOpts,
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            certs: (s.certs as CertFormRow[]).map((c) => (c.key === row.key ? { ...c, issueCountryId: v } : c)),
                          })),
                      )}
                      {renderTextField(
                        'Certificate number',
                        `cert-certNumber-${row.key}`,
                        row.certNumber ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            certs: (s.certs as CertFormRow[]).map((c) => (c.key === row.key ? { ...c, certNumber: v } : c)),
                          })),
                      )}
                    </>,
                  )}
                  {renderInlineFieldGroup(
                    'Validity & status',
                    <>
                      {renderDateField(
                        'Issued date',
                        `cert-issuedDate-${row.key}`,
                        row.issuedDate ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            certs: (s.certs as CertFormRow[]).map((c) => (c.key === row.key ? { ...c, issuedDate: v } : c)),
                          })),
                      )}
                      {renderDateField(
                        'Expiry date',
                        `cert-expiredDate-${row.key}`,
                        row.expiredDate ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            certs: (s.certs as CertFormRow[]).map((c) => (c.key === row.key ? { ...c, expiredDate: v } : c)),
                          })),
                      )}
                      {renderCheckboxField(
                        'On vessel',
                        `cert-onVessel-${row.key}`,
                        row.onVessel ?? false,
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            certs: (s.certs as CertFormRow[]).map((c) => (c.key === row.key ? { ...c, onVessel: v } : c)),
                          })),
                      )}
                    </>,
                  )}
                  {renderInlineFieldGroup(
                    'Files & notes',
                    <>
                      {renderTextField(
                        'File name',
                        `cert-certFileName-${row.key}`,
                        row.certFileName ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            certs: (s.certs as CertFormRow[]).map((c) => (c.key === row.key ? { ...c, certFileName: v } : c)),
                          })),
                      )}
                      {renderTextField(
                        'Notes',
                        `cert-notes-${row.key}`,
                        row.notes ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            certs: (s.certs as CertFormRow[]).map((c) => (c.key === row.key ? { ...c, notes: v } : c)),
                          })),
                        { multiline: true, span: true },
                      )}
                    </>,
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </TabsContent>

      <TabsContent value="allottees" className="mt-0">
        <p className={cn('text-xs mb-3', colors.textMuted)}>
          Allottee amount is the beneficiary instruction; contract allotment is set on the Transactions wage columns.
        </p>
        {!readOnly && (
          <div className="mb-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() =>
                applyForm((s) => ({
                  ...s,
                  allottees: [
                    ...s.allottees,
                    {
                      key: newKey(),
                      fullName: '',
                      relationId: '',
                      phone: '',
                      mobile: '',
                      email: '',
                      bank: '',
                      bankAccount: '',
                      amount: '',
                      currency: '',
                      startDate: '',
                      endDate: '',
                      frequency: '',
                      columnId: '',
                      homeAddress: '',
                    },
                  ],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add allottee
            </Button>
          </div>
        )}
        <div className="space-y-4">
          {form.allottees.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No allottees recorded.</p>
          ) : (
            form.allottees.map((row: AllotteeFormRow) => (
              <Card key={row.key} className="p-4">
                {!readOnly && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Remove allottee"
                      aria-label="Remove allottee"
                      className={cn(colors.textSecondary, 'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25')}
                      onClick={() => removeRow('allottees', row.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <div className="space-y-5">
                  {renderInlineFieldGroup(
                    'Person',
                    <>
                      {renderTextField(
                        'Full name',
                        `allottee-fullName-${row.key}`,
                        row.fullName,
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) => (a.key === row.key ? { ...a, fullName: v } : a)),
                          })),
                        { required: true },
                      )}
                      {renderSelectField(
                        'Relation',
                        `allottee-relationId-${row.key}`,
                        row.relationId,
                        relationOpts,
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) => (a.key === row.key ? { ...a, relationId: v } : a)),
                          })),
                      )}
                    </>,
                  )}
                  {renderInlineFieldGroup(
                    'Contact',
                    <>
                      {renderTextField(
                        'Phone',
                        `allottee-phone-${row.key}`,
                        row.phone ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) => (a.key === row.key ? { ...a, phone: v } : a)),
                          })),
                      )}
                      {renderTextField(
                        'Mobile',
                        `allottee-mobile-${row.key}`,
                        row.mobile ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) => (a.key === row.key ? { ...a, mobile: v } : a)),
                          })),
                      )}
                      {renderTextField(
                        'Email',
                        `allottee-email-${row.key}`,
                        row.email ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) => (a.key === row.key ? { ...a, email: v } : a)),
                          })),
                      )}
                      {renderTextField(
                        'Home address',
                        `allottee-homeAddress-${row.key}`,
                        row.homeAddress ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) => (a.key === row.key ? { ...a, homeAddress: v } : a)),
                          })),
                        { multiline: true, span: true },
                      )}
                    </>,
                  )}
                  {renderInlineFieldGroup(
                    'Banking & amount',
                    <>
                      {renderTextField(
                        'Bank',
                        `allottee-bank-${row.key}`,
                        row.bank ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) => (a.key === row.key ? { ...a, bank: v } : a)),
                          })),
                      )}
                      {renderTextField(
                        'Bank account',
                        `allottee-bankAccount-${row.key}`,
                        row.bankAccount ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) => (a.key === row.key ? { ...a, bankAccount: v } : a)),
                          })),
                      )}
                      {renderNumberField(
                        'Amount',
                        `allottee-amount-${row.key}`,
                        row.amount,
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) => (a.key === row.key ? { ...a, amount: v } : a)),
                          })),
                      )}
                      {currencyOpts.length > 0
                        ? renderSelectField(
                            'Currency',
                            `allottee-currency-${row.key}`,
                            row.currency ?? '',
                            currencyOpts,
                            (v) =>
                              applyForm((s) => ({
                                ...s,
                                allottees: (s.allottees as AllotteeFormRow[]).map((a) =>
                                  a.key === row.key ? { ...a, currency: v } : a,
                                ),
                              })),
                          )
                        : renderTextField(
                            'Currency',
                            `allottee-currency-${row.key}`,
                            row.currency ?? '',
                            (v) =>
                              applyForm((s) => ({
                                ...s,
                                allottees: (s.allottees as AllotteeFormRow[]).map((a) =>
                                  a.key === row.key ? { ...a, currency: v } : a,
                                ),
                              })),
                          )}
                      {renderDateField(
                        'Start date',
                        `allottee-startDate-${row.key}`,
                        row.startDate ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) =>
                              a.key === row.key ? { ...a, startDate: v } : a,
                            ),
                          })),
                      )}
                      {renderDateField(
                        'End date',
                        `allottee-endDate-${row.key}`,
                        row.endDate ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) =>
                              a.key === row.key ? { ...a, endDate: v } : a,
                            ),
                          })),
                      )}
                      {renderSelectField(
                        'Frequency',
                        `allottee-frequency-${row.key}`,
                        row.frequency ?? '',
                        allotteeFrequencyOpts,
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) =>
                              a.key === row.key ? { ...a, frequency: v } : a,
                            ),
                          })),
                      )}
                      {renderSelectField(
                        'Linked wage column',
                        `allottee-columnId-${row.key}`,
                        row.columnId ?? '',
                        contractColumnOpts,
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            allottees: (s.allottees as AllotteeFormRow[]).map((a) =>
                              a.key === row.key ? { ...a, columnId: v } : a,
                            ),
                          })),
                      )}
                    </>,
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </TabsContent>

      <TabsContent value="nok" className="mt-0">
        {!readOnly && (
          <div className="mb-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() =>
                applyForm((s) => ({
                  ...s,
                  nextOfKin: [
                    ...s.nextOfKin,
                    {
                      key: newKey(),
                      fullName: '',
                      relationId: '',
                      phone: '',
                      mobile: '',
                      email: '',
                      homeAddress: '',
                      notes: '',
                    },
                  ],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add next of kin
            </Button>
          </div>
        )}
        <div className="space-y-4">
          {form.nextOfKin.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No next of kin recorded.</p>
          ) : (
            form.nextOfKin.map((row: NokFormRow) => (
              <Card key={row.key} className="p-4">
                {!readOnly && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Remove next of kin"
                      aria-label="Remove next of kin"
                      className={cn(colors.textSecondary, 'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25')}
                      onClick={() => removeRow('nextOfKin', row.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <div className={gridClass}>
                  {renderTextField(
                    'Full name',
                    `nok-fullName-${row.key}`,
                    row.fullName,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        nextOfKin: (s.nextOfKin as NokFormRow[]).map((n) => (n.key === row.key ? { ...n, fullName: v } : n)),
                      })),
                    { required: true },
                  )}
                  {renderSelectField(
                    'Relation',
                    `nok-relationId-${row.key}`,
                    row.relationId,
                    relationOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        nextOfKin: (s.nextOfKin as NokFormRow[]).map((n) => (n.key === row.key ? { ...n, relationId: v } : n)),
                      })),
                  )}
                  {renderTextField(
                    'Phone',
                    `nok-phone-${row.key}`,
                    row.phone ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        nextOfKin: (s.nextOfKin as NokFormRow[]).map((n) => (n.key === row.key ? { ...n, phone: v } : n)),
                      })),
                  )}
                  {renderTextField(
                    'Mobile',
                    `nok-mobile-${row.key}`,
                    row.mobile ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        nextOfKin: (s.nextOfKin as NokFormRow[]).map((n) => (n.key === row.key ? { ...n, mobile: v } : n)),
                      })),
                  )}
                  {renderTextField(
                    'Email',
                    `nok-email-${row.key}`,
                    row.email ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        nextOfKin: (s.nextOfKin as NokFormRow[]).map((n) => (n.key === row.key ? { ...n, email: v } : n)),
                      })),
                  )}
                  {renderTextField(
                    'Home address',
                    `nok-homeAddress-${row.key}`,
                    row.homeAddress ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        nextOfKin: (s.nextOfKin as NokFormRow[]).map((n) => (n.key === row.key ? { ...n, homeAddress: v } : n)),
                      })),
                    { multiline: true, span: true },
                  )}
                  {renderTextField(
                    'Notes',
                    `nok-notes-${row.key}`,
                    row.notes ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        nextOfKin: (s.nextOfKin as NokFormRow[]).map((n) => (n.key === row.key ? { ...n, notes: v } : n)),
                      })),
                    { multiline: true, span: true },
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </TabsContent>

      <TabsContent value="appraisals" className="mt-0">
        {!readOnly && (
          <div className="mb-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() =>
                applyForm((s) => ({
                  ...s,
                  appraisals: [
                    ...s.appraisals,
                    {
                      key: newKey(),
                      appraisalType: '',
                      kindId: '',
                      questionnaireId: '',
                      questionnaireAvg: '',
                      vesselId: '',
                      assessmentDate: '',
                      assessorName: '',
                      employStatusId: '',
                      toBePromoted: false,
                      badHabits: '',
                      dismissalReasonId: '',
                      dismissalReasonText: '',
                      notes: '',
                      appraisalFileName: '',
                      answers: [],
                    },
                  ],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add appraisal
            </Button>
          </div>
        )}
        <div className="space-y-4">
          {form.appraisals.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No appraisals recorded.</p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className={colors.headerBg}>
                    <tr className={cn('text-left', colors.textMuted)}>
                      {['Kind', 'Questionnaire', 'Vessel', 'Date', 'Assessor', 'Avg', 'File'].map((heading) => (
                        <th key={heading} className="px-3 py-2 font-medium">{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.appraisals.map((formRow: AppraisalFormRow) => {
                      const kind = (lookups?.questionnaireKinds ?? []).find((item) => String(item.id) === formRow.kindId);
                      const questionnaire = (lookups?.questionnaires ?? []).find(
                        (item) => String(item.id) === formRow.questionnaireId,
                      );
                      return (
                        <tr key={formRow.key} className={cn('border-t', colors.border, colors.text)}>
                          <td className="px-3 py-2">{kind?.name ?? optionLabel(APPRAISAL_TYPE_OPTIONS, formRow.appraisalType)}</td>
                          <td className="px-3 py-2">{questionnaire?.name ?? '-'}</td>
                          <td className="px-3 py-2">{optionLabel(vesselOpts, formRow.vesselId)}</td>
                          <td className="px-3 py-2">{displayDate(formRow.assessmentDate)}</td>
                          <td className="px-3 py-2">{displayText(formRow.assessorName)}</td>
                          <td className="px-3 py-2">{displayText(formRow.questionnaireAvg)}</td>
                          <td className="px-3 py-2">{displayText(formRow.appraisalFileName)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {form.appraisals.map((row: AppraisalFormRow) => {
              const selectedKind = (lookups?.questionnaireKinds ?? []).find((kind) => String(kind.id) === row.kindId);
              const kindCode = (selectedKind?.code ?? row.appraisalType ?? '').toUpperCase();
              const questionnaireOpts = (lookups?.questionnaires ?? [])
                .filter((questionnaire) => !row.kindId || String(questionnaire.kindId) === row.kindId)
                .map((questionnaire) => ({ value: String(questionnaire.id), label: questionnaire.name }));
              const selectedQuestionnaire = (lookups?.questionnaires ?? []).find(
                (questionnaire) => String(questionnaire.id) === row.questionnaireId,
              );
              const updateRow = (patch: Partial<AppraisalFormRow>) =>
                applyForm((state) => ({
                  ...state,
                  appraisals: (state.appraisals as AppraisalFormRow[]).map((item) =>
                    item.key === row.key ? { ...item, ...patch } : item,
                  ),
                }));
              const selectKind = (kindId: string) => {
                const kind = (lookups?.questionnaireKinds ?? []).find((item) => String(item.id) === kindId);
                const code = (kind?.code ?? '').toUpperCase();
                updateRow({
                  kindId,
                  appraisalType: code === 'APPRAISAL' || code === 'DISCHARGE' ? code : '',
                  questionnaireId: '',
                  questionnaireAvg: '',
                  answers: [],
                  ...(code !== 'APPRAISAL' ? { toBePromoted: false, badHabits: '' } : {}),
                  ...(code !== 'DISCHARGE' ? { dismissalReasonId: '', dismissalReasonText: '' } : {}),
                });
              };
              const selectQuestionnaire = (questionnaireId: string) => {
                const questionnaire = (lookups?.questionnaires ?? []).find((item) => String(item.id) === questionnaireId);
                const priorAnswers = new Map((row.answers ?? []).map((answer) => [answer.questionId, answer]));
                updateRow({
                  questionnaireId,
                  answers: (questionnaire?.questions ?? []).map((question) => {
                    const prior = priorAnswers.get(String(question.id));
                    return {
                      key: prior?.key ?? `ans-${question.id}-${Date.now()}`,
                      questionId: String(question.id),
                      questionLabelSnapshot: question.label,
                      answerTypeSnapshot: question.answerType,
                      scaleMinSnapshot: question.scaleMin != null ? String(question.scaleMin) : '',
                      scaleMaxSnapshot: question.scaleMax != null ? String(question.scaleMax) : '',
                      scaleStepSnapshot: question.scaleStep != null ? String(question.scaleStep) : '',
                      scaleValue: prior?.scaleValue ?? '',
                      choiceValue: prior?.choiceValue ?? '',
                      textValue: prior?.textValue ?? '',
                      comment: prior?.comment ?? '',
                    };
                  }),
                });
              };
              const updateAnswer = (key: string, patch: Partial<AppraisalFormRow['answers'][number]>) =>
                updateRow({ answers: (row.answers ?? []).map((answer) => (answer.key === key ? { ...answer, ...patch } : answer)) });
              return (
              <Card key={row.key} className="p-4">
                {!readOnly && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Remove form"
                      aria-label="Remove form"
                      className={cn(colors.textSecondary, 'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25')}
                      onClick={() => removeRow('appraisals', row.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <div className={gridClass}>
                  {renderSelectField('Kind', `appraisal-kindId-${row.key}`, row.kindId, questionnaireKindOpts, selectKind)}
                  {renderSelectField(
                    'Questionnaire',
                    `appraisal-questionnaireId-${row.key}`,
                    row.questionnaireId,
                    questionnaireOpts,
                    selectQuestionnaire,
                  )}
                  {renderSelectField(
                    'Vessel',
                    `appraisal-vesselId-${row.key}`,
                    row.vesselId,
                    vesselOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        appraisals: (s.appraisals as AppraisalFormRow[]).map((a) => (a.key === row.key ? { ...a, vesselId: v } : a)),
                      })),
                  )}
                  {renderDateField(
                    'Assessment date',
                    `appraisal-assessmentDate-${row.key}`,
                    row.assessmentDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        appraisals: (s.appraisals as AppraisalFormRow[]).map((a) => (a.key === row.key ? { ...a, assessmentDate: v } : a)),
                      })),
                  )}
                  {renderTextField(
                    'Assessor name',
                    `appraisal-assessorName-${row.key}`,
                    row.assessorName ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        appraisals: (s.appraisals as AppraisalFormRow[]).map((a) => (a.key === row.key ? { ...a, assessorName: v } : a)),
                      })),
                  )}
                  {renderSelectField(
                    'Employment status',
                    `appraisal-employStatusId-${row.key}`,
                    row.employStatusId,
                    employmentStatusOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        appraisals: (s.appraisals as AppraisalFormRow[]).map((a) => (a.key === row.key ? { ...a, employStatusId: v } : a)),
                      })),
                  )}
                  {kindCode === 'APPRAISAL' && (
                    <>
                      {renderCheckboxField('To be promoted', `appraisal-toBePromoted-${row.key}`, row.toBePromoted ?? false, (v) =>
                        updateRow({ toBePromoted: v }),
                      )}
                      {renderTextField('Bad habits', `appraisal-badHabits-${row.key}`, row.badHabits ?? '', (v) =>
                        updateRow({ badHabits: v }),
                      { multiline: true, span: true })}
                    </>
                  )}
                  {kindCode === 'DISCHARGE' && (
                    <>
                      {renderSelectField(
                        'Dismissal reason',
                        `appraisal-dismissalReasonId-${row.key}`,
                        row.dismissalReasonId,
                        signOffReasonOpts,
                        (v) => updateRow({ dismissalReasonId: v }),
                      )}
                      {renderTextField(
                        'Dismissal reason text',
                        `appraisal-dismissalReasonText-${row.key}`,
                        row.dismissalReasonText ?? '',
                        (v) => updateRow({ dismissalReasonText: v }),
                      { span: true })}
                    </>
                  )}
                  <div className="col-span-full space-y-3 rounded-lg border p-4">
                    <div>
                      <Label>Answers</Label>
                      <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                        {selectedQuestionnaire ? `Answer the ${selectedQuestionnaire.name} questionnaire.` : 'Select a questionnaire to answer its questions.'}
                      </p>
                    </div>
                    {!selectedQuestionnaire ? null : (row.answers ?? []).length === 0 ? (
                      <p className={cn('text-sm', colors.textMuted)}>This questionnaire has no active questions.</p>
                    ) : (
                      (row.answers ?? []).map((answer) => {
                        const min = Number(answer.scaleMinSnapshot);
                        const max = Number(answer.scaleMaxSnapshot);
                        const step = Number(answer.scaleStepSnapshot || 1);
                        return (
                          <div key={answer.key} className={cn('space-y-3 rounded-lg border p-3', colors.border, colors.headerBg)}>
                            <div>
                              <Label>{answer.questionLabelSnapshot}</Label>
                              <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                                {answer.answerTypeSnapshot.replace(/_/g, ' ')}
                              </p>
                            </div>
                            <div className={gridClass}>
                              {answer.answerTypeSnapshot === 'SCALE' && Number.isFinite(min) && Number.isFinite(max)
                                ? renderSelectField(
                                    'Score',
                                    `appraisal-answer-score-${answer.key}`,
                                    answer.scaleValue,
                                    scaleSelectOptions(min, max, Number.isFinite(step) ? step : 1),
                                    (v) => updateAnswer(answer.key, { scaleValue: v }),
                                  )
                                : null}
                              {answer.answerTypeSnapshot === 'YES_NO_NA'
                                ? renderSelectField(
                                    'Answer',
                                    `appraisal-answer-choice-${answer.key}`,
                                    answer.choiceValue,
                                    CHOICE_OPTIONS,
                                    (v) => updateAnswer(answer.key, { choiceValue: v }),
                                  )
                                : null}
                              {answer.answerTypeSnapshot === 'TEXT'
                                ? renderTextField(
                                    'Answer',
                                    `appraisal-answer-text-${answer.key}`,
                                    answer.textValue,
                                    (v) => updateAnswer(answer.key, { textValue: v }),
                                    { multiline: true, span: true },
                                  )
                                : null}
                              {renderTextField(
                                'Comment',
                                `appraisal-answer-comment-${answer.key}`,
                                answer.comment,
                                (v) => updateAnswer(answer.key, { comment: v }),
                                { span: true },
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {renderTextField(
                    'File name',
                    `appraisal-appraisalFileName-${row.key}`,
                    row.appraisalFileName ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        appraisals: (s.appraisals as AppraisalFormRow[]).map((a) => (a.key === row.key ? { ...a, appraisalFileName: v } : a)),
                      })),
                  )}
                  {renderTextField(
                    'Notes',
                    `appraisal-notes-${row.key}`,
                    row.notes ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        appraisals: (s.appraisals as AppraisalFormRow[]).map((a) => (a.key === row.key ? { ...a, notes: v } : a)),
                      })),
                    { multiline: true, span: true },
                  )}
                </div>
              </Card>
              );
              })}
            </>
          )}
        </div>
      </TabsContent>

      <TabsContent value="feedback" className="mt-0">
        {!readOnly && (
          <div className="mb-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() =>
                applyForm((s) => ({
                  ...s,
                  feedback: [...s.feedback, { key: newKey(), feedbackDate: '', notes: '' }],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add feedback
            </Button>
          </div>
        )}
        <div className="space-y-4">
          {form.feedback.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No feedback recorded.</p>
          ) : (
            form.feedback.map((row: FeedbackFormRow) => (
              <Card key={row.key} className="p-4">
                {!readOnly && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Remove feedback"
                      aria-label="Remove feedback"
                      className={cn(colors.textSecondary, 'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25')}
                      onClick={() => removeRow('feedback', row.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <div className={gridClass}>
                  {renderDateField(
                    'Feedback date',
                    `feedback-feedbackDate-${row.key}`,
                    row.feedbackDate,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        feedback: (s.feedback as FeedbackFormRow[]).map((f) => (f.key === row.key ? { ...f, feedbackDate: v } : f)),
                      })),
                  )}
                  {renderTextField(
                    'Notes',
                    `feedback-notes-${row.key}`,
                    row.notes ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        feedback: (s.feedback as FeedbackFormRow[]).map((f) => (f.key === row.key ? { ...f, notes: v } : f)),
                      })),
                    { multiline: true, span: true },
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </TabsContent>

      <TabsContent value="prevService" className="mt-0">
        {!readOnly && (
          <div className="mb-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() =>
                applyForm((s) => ({
                  ...s,
                  prevServices: [
                    ...s.prevServices,
                    {
                      key: newKey(),
                      vesselName: '',
                      rankId: '',
                      vesselTypeId: '',
                      engTypeId: '',
                      vesselFlagId: '',
                      startDate: '',
                      signOnDate: '',
                      signOnPort: '',
                      signOffDate: '',
                      endDate: '',
                      signOffPort: '',
                      signOffReasonId: '',
                      managerName: '',
                      manningAgency: '',
                      dwt: '',
                      grt: '',
                      nrt: '',
                      engType: '',
                      notes: '',
                    },
                  ],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add previous service
            </Button>
          </div>
        )}
        <div className="space-y-4">
          {form.prevServices.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No previous service recorded.</p>
          ) : (
            form.prevServices.map((row: PrevServiceFormRow) => (
              <Card key={row.key} className="p-4">
                {!readOnly && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Remove previous service"
                      aria-label="Remove previous service"
                      className={cn(colors.textSecondary, 'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25')}
                      onClick={() => removeRow('prevServices', row.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <div className={gridClass}>
                  {renderTextField(
                    'Vessel name',
                    `prev-vesselName-${row.key}`,
                    row.vesselName ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, vesselName: v } : p)),
                      })),
                  )}
                  {renderSelectField(
                    'Rank',
                    `prev-rankId-${row.key}`,
                    row.rankId,
                    rankOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, rankId: v } : p)),
                      })),
                  )}
                  {renderSelectField(
                    'Vessel type',
                    `prev-vesselTypeId-${row.key}`,
                    row.vesselTypeId,
                    vesselTypeOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, vesselTypeId: v } : p)),
                      })),
                  )}
                  {renderSelectField(
                    'Engine type',
                    `prev-engTypeId-${row.key}`,
                    row.engTypeId,
                    engineTypeOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, engTypeId: v } : p)),
                      })),
                  )}
                  {renderSelectField(
                    'Vessel flag',
                    `prev-vesselFlagId-${row.key}`,
                    row.vesselFlagId,
                    countryOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, vesselFlagId: v } : p)),
                      })),
                  )}
                  {renderDateField(
                    'Start date',
                    `prev-startDate-${row.key}`,
                    row.startDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, startDate: v } : p)),
                      })),
                  )}
                  {renderDateField(
                    'Sign on date',
                    `prev-signOnDate-${row.key}`,
                    row.signOnDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, signOnDate: v } : p)),
                      })),
                  )}
                  {renderTextField(
                    'Sign on port',
                    `prev-signOnPort-${row.key}`,
                    row.signOnPort ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, signOnPort: v } : p)),
                      })),
                  )}
                  {renderDateField(
                    'Sign off date',
                    `prev-signOffDate-${row.key}`,
                    row.signOffDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, signOffDate: v } : p)),
                      })),
                  )}
                  {renderDateField(
                    'End date',
                    `prev-endDate-${row.key}`,
                    row.endDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, endDate: v } : p)),
                      })),
                  )}
                  {renderTextField(
                    'Sign off port',
                    `prev-signOffPort-${row.key}`,
                    row.signOffPort ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, signOffPort: v } : p)),
                      })),
                  )}
                  {renderSelectField(
                    'Sign off reason',
                    `prev-signOffReasonId-${row.key}`,
                    row.signOffReasonId,
                    signOffReasonOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) =>
                          p.key === row.key ? { ...p, signOffReasonId: v } : p,
                        ),
                      })),
                  )}
                  {renderTextField(
                    'Manager name',
                    `prev-managerName-${row.key}`,
                    row.managerName ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, managerName: v } : p)),
                      })),
                  )}
                  {renderTextField(
                    'Manning agency',
                    `prev-manningAgency-${row.key}`,
                    row.manningAgency ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, manningAgency: v } : p)),
                      })),
                  )}
                  {renderNumberField(
                    'DWT',
                    `prev-dwt-${row.key}`,
                    row.dwt,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, dwt: v } : p)),
                      })),
                  )}
                  {renderNumberField(
                    'GRT',
                    `prev-grt-${row.key}`,
                    row.grt,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, grt: v } : p)),
                      })),
                  )}
                  {renderNumberField(
                    'NRT',
                    `prev-nrt-${row.key}`,
                    row.nrt,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, nrt: v } : p)),
                      })),
                  )}
                  {renderTextField(
                    'Engine type (text)',
                    `prev-engType-${row.key}`,
                    row.engType ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, engType: v } : p)),
                      })),
                  )}
                  {renderTextField(
                    'Notes',
                    `prev-notes-${row.key}`,
                    row.notes ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        prevServices: (s.prevServices as PrevServiceFormRow[]).map((p) => (p.key === row.key ? { ...p, notes: v } : p)),
                      })),
                    { multiline: true, span: true },
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </TabsContent>

      <TabsContent value="transactions" className="mt-0">
        {!readOnly && (
          <div className="mb-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() =>
                applyForm((s) => ({
                  ...s,
                  transactions: [
                    ...s.transactions,
                    {
                      key: newKey(),
                      status: '',
                      vesselId: '',
                      vesselName: '',
                      rankId: '',
                      startDate: '',
                      signOnDate: '',
                      signOnPort: '',
                      signOffDate: '',
                      endDate: '',
                      signOffPort: '',
                      signOffReasonId: '',
                      manningAgencyId: '',
                      coeDate: '',
                      extCoeDate: '',
                      priorCoeDate: '',
                      latestCoeDate: '',
                      totalWage: '',
                      wageCurrency: '',
                      flWatchkeeper: false,
                      duties: '',
                      includedOt: '',
                      intentionSignOffDate: '',
                      fileName: '',
                      notes: '',
                      wageScaleId: '',
                      accruedLeaveDays: '',
                      leavePayDue: '',
                      finalCashDue: '',
                      repatriation: '',
                      wageValues: {},
                    },
                  ],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add transaction
            </Button>
          </div>
        )}
        <div className="space-y-4">
          {form.transactions.length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No transactions recorded.</p>
          ) : (
            form.transactions.map((row: TransactionFormRow) => (
              <Card key={row.key} className="p-4">
                {!readOnly && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Remove transaction"
                      aria-label="Remove transaction"
                      className={cn(colors.textSecondary, 'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25')}
                      onClick={() => removeRow('transactions', row.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <div className={gridClass}>
                  {renderSelectField(
                    'Status',
                    `txn-status-${row.key}`,
                    row.status,
                    TRANSACTION_STATUS_OPTIONS,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, status: v } : t)),
                      })),
                  )}
                  {renderSelectField(
                    'Vessel',
                    `txn-vesselId-${row.key}`,
                    row.vesselId,
                    vesselOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, vesselId: v } : t)),
                      })),
                  )}
                  {renderTextField(
                    'Vessel name',
                    `txn-vesselName-${row.key}`,
                    row.vesselName ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, vesselName: v } : t)),
                      })),
                  )}
                  {renderSelectField(
                    'Rank',
                    `txn-rankId-${row.key}`,
                    row.rankId,
                    rankOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, rankId: v } : t)),
                      })),
                  )}
                  {!readOnly && (
                    <div className="min-w-0 flex flex-col justify-end gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={wageImportingKey === row.key}
                        onClick={() => void importWageScale(row)}
                      >
                        {wageImportingKey === row.key ? 'Importing…' : 'Import wage scale'}
                      </Button>
                      {wageImportErrors[row.key] && (
                        <p className="text-xs text-red-600 dark:text-red-400">{wageImportErrors[row.key]}</p>
                      )}
                    </div>
                  )}
                  {renderDateField(
                    'Start date',
                    `txn-startDate-${row.key}`,
                    row.startDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, startDate: v } : t)),
                      })),
                  )}
                  {renderDateField(
                    'Sign on date',
                    `txn-signOnDate-${row.key}`,
                    row.signOnDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, signOnDate: v } : t)),
                      })),
                  )}
                  {renderTextField(
                    'Sign on port',
                    `txn-signOnPort-${row.key}`,
                    row.signOnPort ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, signOnPort: v } : t)),
                      })),
                  )}
                  {renderDateField(
                    'Sign off date',
                    `txn-signOffDate-${row.key}`,
                    row.signOffDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, signOffDate: v } : t)),
                      })),
                  )}
                  {renderDateField(
                    'End date',
                    `txn-endDate-${row.key}`,
                    row.endDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, endDate: v } : t)),
                      })),
                  )}
                  {renderTextField(
                    'Sign off port',
                    `txn-signOffPort-${row.key}`,
                    row.signOffPort ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, signOffPort: v } : t)),
                      })),
                  )}
                  {renderSelectField(
                    'Sign off reason',
                    `txn-signOffReasonId-${row.key}`,
                    row.signOffReasonId,
                    signOffReasonOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) =>
                          t.key === row.key ? { ...t, signOffReasonId: v } : t,
                        ),
                      })),
                  )}
                  {renderSelectField(
                    'Manning agency',
                    `txn-manningAgencyId-${row.key}`,
                    row.manningAgencyId,
                    manningAgentOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) =>
                          t.key === row.key ? { ...t, manningAgencyId: v } : t,
                        ),
                      })),
                  )}
                  {renderDateField(
                    'COE date',
                    `txn-coeDate-${row.key}`,
                    row.coeDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, coeDate: v } : t)),
                      })),
                  )}
                  {renderDateField(
                    'Extended COE date',
                    `txn-extCoeDate-${row.key}`,
                    row.extCoeDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, extCoeDate: v } : t)),
                      })),
                  )}
                  {renderDateField(
                    'Prior COE date',
                    `txn-priorCoeDate-${row.key}`,
                    row.priorCoeDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, priorCoeDate: v } : t)),
                      })),
                  )}
                  {renderDateField(
                    'Latest COE date',
                    `txn-latestCoeDate-${row.key}`,
                    row.latestCoeDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, latestCoeDate: v } : t)),
                      })),
                  )}
                  {currencyOpts.length > 0
                    ? renderSelectField(
                        'Wage currency',
                        `txn-wageCurrency-${row.key}`,
                        row.wageCurrency ?? '',
                        currencyOpts,
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            transactions: (s.transactions as TransactionFormRow[]).map((t) =>
                              t.key === row.key ? { ...t, wageCurrency: v } : t,
                            ),
                          })),
                      )
                    : renderTextField(
                        'Wage currency',
                        `txn-wageCurrency-${row.key}`,
                        row.wageCurrency ?? '',
                        (v) =>
                          applyForm((s) => ({
                            ...s,
                            transactions: (s.transactions as TransactionFormRow[]).map((t) =>
                              t.key === row.key ? { ...t, wageCurrency: v } : t,
                            ),
                          })),
                      )}
                  {contractColumns.map((column) =>
                    renderNumberField(
                      column.name,
                      `txn-wageCol-${column.id}-${row.key}`,
                      row.wageValues?.[String(column.id)] ?? '',
                      (v) => setTransactionWageValue(row.key, String(column.id), v),
                    ),
                  )}
                  <div className="min-w-0">
                    <Label>Total wage</Label>
                    <span className={readOnlyTextClass}>{displayText(row.totalWage)}</span>
                  </div>
                  {renderNumberField(
                    'Accrued leave days',
                    `txn-accruedLeaveDays-${row.key}`,
                    row.accruedLeaveDays ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) =>
                          t.key === row.key ? { ...t, accruedLeaveDays: v } : t,
                        ),
                      })),
                  )}
                  {renderNumberField(
                    'Leave pay due',
                    `txn-leavePayDue-${row.key}`,
                    row.leavePayDue ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) =>
                          t.key === row.key ? { ...t, leavePayDue: v } : t,
                        ),
                      })),
                  )}
                  {renderNumberField(
                    'Final cash due',
                    `txn-finalCashDue-${row.key}`,
                    row.finalCashDue ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) =>
                          t.key === row.key ? { ...t, finalCashDue: v } : t,
                        ),
                      })),
                  )}
                  {renderTextField(
                    'Repatriation',
                    `txn-repatriation-${row.key}`,
                    row.repatriation ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) =>
                          t.key === row.key ? { ...t, repatriation: v } : t,
                        ),
                      })),
                  )}
                  {renderCheckboxField(
                    'Flag state watchkeeper',
                    `txn-flWatchkeeper-${row.key}`,
                    row.flWatchkeeper ?? false,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, flWatchkeeper: v } : t)),
                      })),
                  )}
                  {renderTextField(
                    'Duties',
                    `txn-duties-${row.key}`,
                    row.duties ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, duties: v } : t)),
                      })),
                    { multiline: true, span: true },
                  )}
                  {renderNumberField(
                    'Included OT',
                    `txn-includedOt-${row.key}`,
                    row.includedOt,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, includedOt: v } : t)),
                      })),
                  )}
                  {renderDateField(
                    'Intention sign off date',
                    `txn-intentionSignOffDate-${row.key}`,
                    row.intentionSignOffDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) =>
                          t.key === row.key ? { ...t, intentionSignOffDate: v } : t,
                        ),
                      })),
                  )}
                  {renderTextField(
                    'File name',
                    `txn-fileName-${row.key}`,
                    row.fileName ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, fileName: v } : t)),
                      })),
                  )}
                  {renderTextField(
                    'Notes',
                    `txn-notes-${row.key}`,
                    row.notes ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        transactions: (s.transactions as TransactionFormRow[]).map((t) => (t.key === row.key ? { ...t, notes: v } : t)),
                      })),
                    { multiline: true, span: true },
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </TabsContent>

      <TabsContent value="medicals" className="mt-0">
        {!readOnly && (
          <div className="mb-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() =>
                applyForm((s) => ({
                  ...s,
                  medicals: [
                    ...(s.medicals ?? []),
                    {
                      key: newKey(),
                      kind: 'FITNESS',
                      examDate: '',
                      nextDueDate: '',
                      result: '',
                      resultNotes: '',
                      clinicName: '',
                      certificateNo: '',
                      notes: '',
                    },
                  ],
                }))
              }
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Add medical
            </Button>
          </div>
        )}
        <div className="space-y-4">
          {(form.medicals ?? []).length === 0 ? (
            <p className={cn('text-sm', colors.textMuted)}>No medical records.</p>
          ) : (
            (form.medicals as MedicalFormRow[]).map((row) => (
              <Card key={row.key} className="p-4">
                {!readOnly && (
                  <div className="mb-3 flex justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Remove medical"
                      aria-label="Remove medical"
                      className={cn(colors.textSecondary, 'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25')}
                      onClick={() => removeRow('medicals', row.key)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <div className={gridClass}>
                  {renderSelectField(
                    'Kind',
                    `medical-kind-${row.key}`,
                    row.kind,
                    medicalKindOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        medicals: (s.medicals as MedicalFormRow[]).map((m) => (m.key === row.key ? { ...m, kind: v } : m)),
                      })),
                  )}
                  {renderDateField(
                    'Exam date',
                    `medical-examDate-${row.key}`,
                    row.examDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        medicals: (s.medicals as MedicalFormRow[]).map((m) => (m.key === row.key ? { ...m, examDate: v } : m)),
                      })),
                  )}
                  {renderDateField(
                    'Next due date',
                    `medical-nextDueDate-${row.key}`,
                    row.nextDueDate ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        medicals: (s.medicals as MedicalFormRow[]).map((m) => (m.key === row.key ? { ...m, nextDueDate: v } : m)),
                      })),
                  )}
                  {renderSelectField(
                    'Result',
                    `medical-result-${row.key}`,
                    row.result,
                    medicalResultOpts,
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        medicals: (s.medicals as MedicalFormRow[]).map((m) => (m.key === row.key ? { ...m, result: v } : m)),
                      })),
                  )}
                  {renderTextField(
                    'Result notes',
                    `medical-resultNotes-${row.key}`,
                    row.resultNotes ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        medicals: (s.medicals as MedicalFormRow[]).map((m) => (m.key === row.key ? { ...m, resultNotes: v } : m)),
                      })),
                  )}
                  {renderTextField(
                    'Clinic name',
                    `medical-clinicName-${row.key}`,
                    row.clinicName ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        medicals: (s.medicals as MedicalFormRow[]).map((m) => (m.key === row.key ? { ...m, clinicName: v } : m)),
                      })),
                  )}
                  {renderTextField(
                    'Certificate no.',
                    `medical-certificateNo-${row.key}`,
                    row.certificateNo ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        medicals: (s.medicals as MedicalFormRow[]).map((m) => (m.key === row.key ? { ...m, certificateNo: v } : m)),
                      })),
                  )}
                  {renderTextField(
                    'Notes',
                    `medical-notes-${row.key}`,
                    row.notes ?? '',
                    (v) =>
                      applyForm((s) => ({
                        ...s,
                        medicals: (s.medicals as MedicalFormRow[]).map((m) => (m.key === row.key ? { ...m, notes: v } : m)),
                      })),
                    { multiline: true, span: true },
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      </TabsContent>

      <TabsContent value="documents" className="mt-0">
        {!crewId ? (
          <p className={cn('text-sm', colors.textMuted)}>Save crew first to upload documents.</p>
        ) : (
          <div className="space-y-4">
            {!readOnly && (
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-0">
                  <Label htmlFor="crew-doc-upload">Files</Label>
                  <input
                    ref={fileInputRef}
                    id="crew-doc-upload"
                    type="file"
                    multiple
                    className={cn('block text-sm', colors.text)}
                    onChange={(e) => void uploadDocuments(e.target.files)}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={docUploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {docUploading ? 'Uploading…' : 'Upload'}
                </Button>
              </div>
            )}
            {docError && <p className="text-xs text-red-600 dark:text-red-400">{docError}</p>}
            {documents.length === 0 ? (
              <p className={cn('text-sm', colors.textMuted)}>No documents uploaded.</p>
            ) : (
              <div className="space-y-2">
                {documents.map((doc) => (
                  <Card key={doc.id} className="p-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className={cn('text-sm font-medium truncate', colors.text)}>{doc.originalFilename}</p>
                      <p className={cn('text-xs', colors.textMuted)}>
                        {[doc.kind, formatFileSize(doc.sizeBytes)].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button type="button" variant="outline" size="sm" onClick={() => void downloadDocument(doc)}>
                        Download
                      </Button>
                      {!readOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          title="Delete document"
                          aria-label="Delete document"
                          className={cn(colors.textSecondary, 'hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/25')}
                          onClick={() => void deleteDocument(doc.id)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </TabsContent>
        </div>
      </div>
    </Tabs>
  );
};

export default CrewFileTabs;
